import { type FC, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import {
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

interface ScratchInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface InfoSection {
  icon: string;
  title: string;
  body: ReactNode;
}

/**
 * Bottom-sheet rules screen for the Scratch event. Mirrors the Bingo
 * info modal layout (handle → header → list → "Got it" CTA) so the
 * pattern stays consistent across game events.
 */
const ScratchInfoModal: FC<ScratchInfoModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const offers = useAppSelector((s) => s.scratch.offers);

  const totalPurchasesPerDay = offers.reduce((s, o) => s + o.maxPurchases, 0);
  // Display-only odds requested by game design; they intentionally do not
  // follow the server's multiplierOdds.
  const onePct = 40;
  const pTwoPct = 40;
  const pThreePct = 20;

  const sections: InfoSection[] = [
    {
      icon: '🎟️',
      title: t('How the event works'),
      body: t(
        'Pick a target reward in any of the three packs, buy a ticket, then scratch the foil. Each ticket has 3 cells in a row.',
      ),
    },
    {
      icon: '⭐',
      title: t('Guaranteed reward'),
      body: t(
        'At least one of the three cells is always your chosen reward. Worst case you walk away with 1× the prize — never empty-handed.',
      ),
    },
    {
      icon: '🎯',
      title: t('Multiplier odds'),
      body: (
        <>
          <StringXSmall className={css.itemDesc}>
            {t(
              'The odds of getting 2 or 3 matching cells are identical across all three packs:',
            )}
          </StringXSmall>
          <ul className={css.bulletList}>
            <li>
              <StringXSmall className={css.itemDesc}>
                {t('1 match (1× reward)')} — <b>{onePct}%</b>
              </StringXSmall>
            </li>
            <li>
              <StringXSmall className={css.itemDesc}>
                {t('2 matches (2× reward)')} — <b>{pTwoPct}%</b>
              </StringXSmall>
            </li>
            <li>
              <StringXSmall className={css.itemDesc}>
                {t('3 matches (jackpot, 3× reward)')} — <b>{pThreePct}%</b>
              </StringXSmall>
            </li>
          </ul>
        </>
      ),
    },
    {
      icon: '💎',
      title: t('Three packs'),
      body: t(
        'Basic pack ($0.99) — common rewards. Standard pack ($1.99) — better rewards. Premium pack ($4.99) — top rewards. Higher-tier packs unlock bigger prize pools, NOT better odds.',
      ),
    },
    {
      icon: '🔒',
      title: t('Purchase limits'),
      body: t(
        'Each pack is capped at 3 buys per event. Total {{total}} tickets per event across all packs. Limits reset when the event ends and refresh when the next event starts.',
        { total: totalPurchasesPerDay },
      ),
    },
    {
      icon: '⏳',
      title: t('Event duration'),
      body: t(
        'The Scratch event runs every Wednesday and Saturday from 00:00 to 23:59 UTC — a 24-hour window each time. All unscratched tickets are auto-revealed and credited to your balance when the event ends.',
      ),
    },
  ];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={css.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className={css.sheet}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className={css.handle} />

            <div className={css.header}>
              <div className={css.headerIcon}>
                <span className={css.headerIconEmoji}>🎟️</span>
              </div>
              <div className={css.headerText}>
                <H2 weight="bold" className={css.headerTitle}>
                  {t('About Scratch event')}
                </H2>
                <StringXSmall className={css.headerSub}>
                  {t('Rules, prizes and limits')}
                </StringXSmall>
              </div>
            </div>

            <div className={css.list}>
              {sections.map((section, idx) => (
                <motion.div
                  key={section.title}
                  className={css.item}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 + idx * 0.04, duration: 0.25 }}
                >
                  <span className={css.itemIcon}>{section.icon}</span>
                  <div className={css.itemText}>
                    <StringMain weight="bold" className={css.itemTitle}>
                      {section.title}
                    </StringMain>
                    {typeof section.body === 'string' ? (
                      <StringXSmall className={css.itemDesc}>
                        {section.body}
                      </StringXSmall>
                    ) : (
                      section.body
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <button className={css.cancel} onClick={onClose}>
              <StringMain className={css.cancelText}>{t('Got it')}</StringMain>
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export { ScratchInfoModal };
